
class CorkProductNavigation extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('.header-wrapper');
    this.triggers = [...this.querySelectorAll('[data-cs-panel]')];
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.accessoryLink = this.root.querySelector('[data-cs-accessories]');
    this.accessoryImages = [...(this.accessoryLink?.querySelectorAll('img') || [])];
    this.accessoryImages.forEach((image, index) => image.classList.toggle('is-active', index === 0));
    this.root.addEventListener('pointerenter', () => window.clearTimeout(this.hoverCloseTimer), options);
    this.root.addEventListener('pointerleave', event => {
      if (event.pointerType !== 'mouse') return;
      this.hoverCloseTimer = window.setTimeout(() => {
        if (!this.panel?.contains(document.activeElement)) this.close(false);
      }, 180);
    }, options);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.accessoryLink?.addEventListener('pointerenter', event => {
      if (event.pointerType === 'touch' || !window.matchMedia('(hover: hover)').matches) return;
      this.changeAccessoryImage();
      if (!this.reducedMotion.matches && this.accessoryImages.length > 1) {
        this.stopAccessoryCycle();
        this.accessoryTimer = window.setInterval(() => this.changeAccessoryImage(), 1800);
      }
    }, options);
    this.accessoryLink?.addEventListener('pointerleave', () => this.stopAccessoryCycle(), options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAccessoryCycle();
    }, options);
    this.reducedMotion.addEventListener('change', () => this.stopAccessoryCycle(), options);
    this.triggers.forEach(trigger => {
      trigger.setAttribute('role', 'button');
      trigger.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        window.clearTimeout(this.hoverCloseTimer);
        if (trigger.getAttribute('aria-expanded') !== 'true') this.toggle(trigger);
      }, options);
      trigger.addEventListener('click', event => {
        event.preventDefault();
        this.toggle(trigger);
      }, options);
      trigger.addEventListener('keydown', event => {
        if (event.key === ' ') {
          event.preventDefault();
          this.toggle(trigger);
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          if (trigger.getAttribute('aria-expanded') !== 'true') this.toggle(trigger);
          this.panel?.querySelector('a, [tabindex="0"]')?.focus();
        }
      }, options);
    });
    this.root.addEventListener('click', event => {
      const designToggle = event.target.closest('.cs-mat-design__toggle');
      if (designToggle) {
        const showLifestyle = designToggle.getAttribute('aria-pressed') !== 'true';
        designToggle.closest('.cs-mat-design-card').classList.toggle('is-lifestyle', showLifestyle);
        designToggle.setAttribute('aria-pressed', String(showLifestyle));
          designToggle.setAttribute('aria-label', showLifestyle ? designToggle.dataset.productLabel : designToggle.dataset.lifestyleLabel);
      }

      if (event.target.closest('.cs-nav-panel__close')) this.close(true);
      if (event.target.closest('.header__search summary')) this.close(false);
    }, options);
    document.addEventListener('click', event => {
      if (this.panel && !this.root.contains(event.target)) this.close(false);
    }, options);
    this.root.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.panel) {
        event.preventDefault();
        this.close(true);
      }
    }, options);
    this.updateViewport = () => {
      const header = this.root.querySelector('.header');
      if (!header) return;
      const viewport = window.visualViewport;
      const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const panelSpace = Math.max(0, Math.floor(viewportBottom - header.getBoundingClientRect().bottom));
      this.root.style.setProperty('--cs-nav-panel-space', `${panelSpace}px`);
    };
    this.scheduleViewport = () => {
      cancelAnimationFrame(this.viewportFrame);
      this.viewportFrame = requestAnimationFrame(this.updateViewport);
    };
    window.addEventListener('resize', this.updateViewport, options);
    window.addEventListener('scroll', this.scheduleViewport, { ...options, passive: true });
    window.visualViewport?.addEventListener('resize', this.updateViewport, options);
    window.visualViewport?.addEventListener('scroll', this.updateViewport, options);
    const stickySection = this.root.closest('.section-header') || this.root;
    this.positionObserver = new MutationObserver(this.scheduleViewport);
    this.positionObserver.observe(stickySection, { attributes: true, attributeFilter: ['class'] });
    stickySection.addEventListener('transitionend', this.updateViewport, options);
    this.observer = new ResizeObserver(() => {
      this.updateViewport();
      document.documentElement.style.setProperty('--header-height', `${this.root.offsetHeight}px`);
    });
    this.observer.observe(this.root);
  }
  toggle(trigger) {
    const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
    if (wasOpen) {
      this.close(false);
      return;
    }
    this.close(false, true);
    this.panel = this.root.querySelector(`#${CSS.escape(trigger.dataset.csPanel)}`);
    if (!this.panel) return;
    this.panel.querySelectorAll('img[loading="lazy"]').forEach(image => {
      image.loading = 'eager';
      image.decoding = 'async';
    });
    this.trigger = trigger;
    this.updateViewport();
    this.animatePanel(this.panel, true);
    if (this.panel.contains(this.accessoryLink)) this.changeAccessoryImage();
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('role', 'button');
    this.root.preventHide = true;
    this.triggers.forEach(item => item.setAttribute('role', 'button'));
  }
  animatePanel(panel, opening, immediate = false) {
    panel.getAnimations().forEach(animation => animation.cancel());
    panel.inert = !opening;
    if (immediate || this.reducedMotion.matches) {
      panel.hidden = !opening;
      return;
    }
    panel.hidden = false;
    const frames = [
      { opacity: 0, transform: 'translateY(-6px)' },
      { opacity: 1, transform: 'translateY(0)' }
    ];
    const animation = panel.animate(opening ? frames : frames.slice().reverse(), {
      duration: opening ? 160 : 120,
      easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)'
    });
    animation.onfinish = () => { panel.hidden = !opening; };
  }
  close(restoreFocus, immediate = false) {
    window.clearTimeout(this.hoverCloseTimer);
    this.stopAccessoryCycle();
    if (!this.panel) return;
    this.animatePanel(this.panel, false, immediate);
    this.trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.trigger.focus();
    this.panel = null;
    this.root.preventHide = false;
  }
  changeAccessoryImage() {
    const candidates = this.accessoryImages.filter(image =>
      image.complete && image.naturalWidth > 0 && !image.classList.contains('is-active')
    );
    if (!candidates.length) return;
    const next = candidates[Math.floor(Math.random() * candidates.length)];
    this.accessoryImages.forEach(image => image.classList.toggle('is-active', image === next));
  }
  stopAccessoryCycle() {
    window.clearInterval(this.accessoryTimer);
    this.accessoryTimer = null;
  }
  disconnectedCallback() {
    this.close(false, true);
    this.controller?.abort();
    this.observer?.disconnect();
    this.positionObserver?.disconnect();
    cancelAnimationFrame(this.viewportFrame);
  }
}
if (!customElements.get('cork-product-navigation')) customElements.define('cork-product-navigation', CorkProductNavigation);
